/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect } from 'react';
import ApiService from "./services/ApiService";
import DataModel from './model/DataModel';

interface CacheBlockProps {
  show: boolean;
  showClearButton: boolean;
  dataModel: DataModel;
}

const CacheBlock: React.FC<CacheBlockProps> = ({ show, showClearButton, dataModel }) => {

  const [, setForceUpdate] = useState(0); // Dummy state to force re-renders
  const [cacheCleared, setCacheCleared] = useState(false);

  useEffect(() => {
    // This update just sets a member variable to force a redraw:
    const update = () => setForceUpdate((prev) => prev + 1);
    // Cleanup on unmount
    return dataModel.subscribe(update);
  }, [dataModel]);

  
  if (!show) {
    return null;
  }

  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Data         </th><th>Cached                              </th><th> </th><th>Out of                             </th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Master List  </td><td>{dataModel.documents.size}          </td><td>/</td><td>{dataModel.documents.size || '?'}  </td><td></td></tr>
          <tr><td>Monsters     </td><td>{dataModel.monsters.data.length}    </td><td>/</td><td>{dataModel.monsters.totalCount}    </td><td>{dataModel.monsters.progress}    </td></tr>
          <tr><td>Monsters 2024</td><td>{dataModel.creatures.data.length}   </td><td>/</td><td>{dataModel.creatures.totalCount}   </td><td>{dataModel.creatures.progress}   </td></tr>
          <tr><td>Magic Items  </td><td>{dataModel.magicItems.data.length}  </td><td>/</td><td>{dataModel.magicItems.totalCount}  </td><td>{dataModel.magicItems.progress}  </td></tr>
          <tr><td>Races        </td><td>{dataModel.races.data.length}       </td><td>/</td><td>{dataModel.races.totalCount}       </td><td>{dataModel.races.progress}       </td></tr>
          <tr><td>Classes      </td><td>{dataModel.classes.data.length}     </td><td>/</td><td>{dataModel.classes.totalCount}     </td><td>{dataModel.classes.progress}     </td></tr>
          <tr><td>Backgrounds  </td><td>{dataModel.backgrounds.data.length} </td><td>/</td><td>{dataModel.backgrounds.totalCount} </td><td>{dataModel.backgrounds.progress} </td></tr>
          <tr><td>Spells       </td><td>{dataModel.spells.data.length}      </td><td>/</td><td>{dataModel.spells.totalCount}      </td><td>{dataModel.spells.progress}      </td></tr>
          <tr><td>Feats        </td><td>{dataModel.feats.data.length}       </td><td>/</td><td>{dataModel.feats.totalCount}       </td><td>{dataModel.feats.progress}       </td></tr>
        </tbody>
      </table>
      <br/>
      Once this data has loaded, it is stored locally in your browser cache, and won't be retreived again until the source content changes.<br/>
      {showClearButton && !cacheCleared && <button onClick={() => { ApiService.clearAllCaches(); dataModel.clear(); setCacheCleared(true); }}>Clear Caches</button>}
      {showClearButton && cacheCleared && <button onClick={() => { setCacheCleared(false); dataModel.fetchAll(false); }}>Load Caches</button>}
    </>
  );
};

export default CacheBlock;
